<template>
  <div
    class="flex md:flex-row flex-col w-full max-w-screen-xl mx-auto h-full items-center pt-8 pb-6"
  >
    <header
      class="absolute inset-0 h-8 font-londrina text-indigo-50 w-full pt-6 px-6 flex justify-start items-center"
    >
      <button
        type="button"
        class="text-sm font-sans ml-6"
        @click="$refs.modal.show()"
      >
        How to play
      </button>
    </header>
    <div class="w-full p-6 md:p-0 md:w-2/5 flex-0 text-center">
      <h1 class="text-4xl font-londrina text-indigo-50 padding-notch">
        Guess the Board Game
      </h1>
      <form @submit.prevent="onSubmit">
        <input
          type="text"
          v-model="current"
          :placeholder="guessPlaceholder"
          :disabled="!canGuess"
          :class="`border bg-transparent text-white font-bold shadow-lg rounded px-4 py-2 my-4 ${
            !canGuess ? 'bg-gray-600 opacity-50' : ''
          }`"
        />
        <button
          type="submit"
          class="bg-indigo-100 px-4 py-2 rounded border shadow-lg"
        >
          Enter!
        </button>
      </form>
      <div
        :class="`relative border-2 border-dashed rounded shadow-lg w-full md:w-1/2 mx-auto h-12 flex justify-center items-center mb-4 transform transition ease-in duration-1000 ${
          isCorrect(guess) ? 'border-emerald-500 tada-animation' : ''
        } ${guess && !isCorrect(guess) ? 'border-red-500' : ''}`"
        v-for="(guess, index) in fixedGuesses"
        :key="index"
      >
        <span v-if="guess" class="text-white font-bold">{{ guess }}</span>
        <span v-else class="text-gray-300 opacity-40"
          >Guess #{{ index + 1 }}</span
        >
        <div
          :class="`absolute top-0 right-0 -mt-2 -mr-2 w-6 h-6 rotate-12 bg-amber-400 rounded-full flex justify-center items-center text-xs font-londrina transform transition delay-1000 duration-500 ${
            guess && !isCorrect(guess) ? 'opacity-0 translate-y-8' : ''
          } ${guess && isCorrect(guess) ? 'scale-150' : ''}`"
          v-if="index < 2"
        >
          {{ index === 0 ? '2.5x' : '2x' }}
        </div>
      </div>
      <div class="mt-6">
        <h2 class="text-2xl font-sans text-indigo-50 inline">Score:</h2>
        <span class="pl-2 text-3xl font-londrina text-indigo-50">
          {{ finalScore }}
        </span>
        <div class="font-sans text-indigo-50 mt-4 px-6 text-lg">
          <p v-if="won">
            Congratulations! You correctly guessed the game
            <br />
            <span class="font-londrina">{{ game }}</span>
            <br />
            See you tomorrow for a new game!
          </p>
          <p v-if="!canGuess && !won">
            Better luck tomorrow! The correct answer was:
            <br />
            <span class="font-londrina">{{ game }}</span>
          </p>
        </div>
        <button
          class="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-gray-800 font-bold rounded mt-2"
          v-if="won || !canGuess"
          type="button"
          @click="shareScore"
        >
          Share Result
        </button>
        <span class="text-xs" v-if="copied">Results copied to clipboard</span>
      </div>
    </div>
    <div
      class="w-full md:w-3/5 md:mr-6 md: ml-0 mx-2 flex-0 h-full flex flex-row flex-wrap justify-between items-stretch pb-6"
    >
      <div class="w-1/2 p-2">
        <hint-tile
          label="Designer"
          :isRevealed="revealed.designer || false"
          :short="details.designer.length < 20"
          @reveal="reveal('designer')"
          v-slot="slotProps"
          :bonus="scoreBonus['designer']"
          :won="won"
        >
          {{
            !revealed.designer && !won
              ? slotProps.obscure(details.designer)
              : details.designer
          }}
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Publisher"
          :isRevealed="revealed.publisher || false"
          @reveal="reveal('publisher')"
          v-slot="slotProps"
          :bonus="scoreBonus['publisher']"
          :won="won"
        >
          <template v-if="!revealed.publisher && !won">
            {{ slotProps.obscure(details.publishers[0]) }}
          </template>
          <template v-else>
            {{ details.publishers[0] }}
            <span
              class="cursor-pointer underline"
              v-if="details.publishers.length > 1"
              v-tooltip="{
                content: details.publishers
                  .map((p) => `<div>${p}</div>`)
                  .join(''),
                container: 'body',
                trigger: 'click',
              }"
            >
              and {{ details.publishers.length - 1 }} other{{
                details.publishers.length > 2 ? 's' : ''
              }}
            </span>
          </template>
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Year"
          :isRevealed="revealed.year || false"
          short
          @reveal="reveal('year')"
          v-slot="slotProps"
          :bonus="scoreBonus['year']"
          :won="won"
        >
          {{
            !revealed.year && !won
              ? slotProps.obscure(String(details.year))
              : details.year
          }}
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Artist"
          :isRevealed="revealed.artist || false"
          @reveal="reveal('artist')"
          v-slot="slotProps"
          :bonus="scoreBonus['artist']"
          :won="won"
        >
          {{
            !revealed.artist && !won
              ? slotProps.obscure(details.artist)
              : details.artist
          }}
        </hint-tile>
      </div>
      <div class="w-1/3 p-2">
        <hint-tile
          label="Player Count"
          :isRevealed="revealed.playerCount || false"
          short
          @reveal="reveal('playerCount')"
          v-slot="slotProps"
          :bonus="scoreBonus['playerCount']"
          :won="won"
        >
          {{
            !revealed.playerCount && !won
              ? slotProps.obscure(details.playerCount)
              : details.playerCount
          }}
        </hint-tile>
      </div>
      <div class="w-1/3 p-2">
        <hint-tile
          label="Playing time"
          :isRevealed="revealed.duration || false"
          short
          @reveal="reveal('duration')"
          v-slot="slotProps"
          :bonus="scoreBonus['duration']"
          :won="won"
        >
          {{
            !revealed.duration && !won
              ? slotProps.obscure(details.duration)
              : durationFixed
          }}
        </hint-tile>
      </div>
      <div class="w-1/3 p-2">
        <hint-tile
          label="First letter"
          :isRevealed="revealed.firstLetter || false"
          short
          @reveal="reveal('firstLetter')"
          v-slot="slotProps"
          :bonus="scoreBonus['firstLetter']"
          :won="won"
        >
          {{
            !revealed.firstLetter && !won
              ? slotProps.obscure(details.firstLetter)
              : details.firstLetter
          }}
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Description"
          :isRevealed="revealed.description || false"
          @reveal="reveal('description')"
          v-slot="slotProps"
          :bonus="scoreBonus['description']"
          :won="won"
        >
          {{
            !revealed.description && !won
              ? slotProps.obscure(details.description)
              : details.description
          }}
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Mechanisms"
          :isRevealed="revealed.mechanisms || false"
          @reveal="reveal('mechanisms')"
          v-slot="slotProps"
          :bonus="scoreBonus['mechanisms']"
          :won="won"
        >
          {{
            !revealed.mechanisms && !won
              ? slotProps.obscure(details.mechanisms.join(', '))
              : details.mechanisms.join(', ')
          }}
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Categories"
          :isRevealed="revealed.categories || false"
          @reveal="reveal('categories')"
          v-slot="slotProps"
          :bonus="scoreBonus['categories']"
          :won="won"
        >
          {{
            !revealed.categories && !won
              ? slotProps.obscure(details.categories.join(', '))
              : details.categories.join(', ')
          }}
        </hint-tile>
      </div>
      <div class="w-1/2 p-2">
        <hint-tile
          label="Board Game Geek Rank"
          :isRevealed="revealed.rank || false"
          @reveal="reveal('rank')"
          v-slot="slotProps"
          :bonus="scoreBonus['rank']"
          :won="won"
        >
          {{
            !revealed.rank && !won
              ? slotProps.obscure(details.rank)
              : details.rank
          }}
        </hint-tile>
      </div>
    </div>
    <modal
      ref="modal"
      noClose
      confirmText="Start the game"
      title="Guess the Board Game"
    >
      <h1 class="font-londrina text-xl mb-2">Goal of the game?</h1>
      <p>It's in the name: Guess the Board Game.</p>
      <h1 class="font-londrina text-xl my-2">Scoring</h1>
      <ul class="list-disc pl-4">
        <li>100 points for correctly guessing the game</li>
        <li>
          Extra points for each hint that remained unrevealed (some hints are
          worth more than others — as shown next to the hint).
        </li>
        <li>
          2.5x or 2x multiplier on the final score if you guess on the first or
          second try
        </li>
      </ul>
      <h1 class="font-londrina text-xl my-2">How to play?</h1>
      <p>
        Choose (click) which details you want to reveal to help you guess the
        name of the game.
      </p>
      <p>
        You can reveal as many as you'd like, but each revealed hint deducts
        from your final score, and some hints have a larger point penalty than
        others.
      </p>
      <p>
        Once you’re ready to guess, enter the title of the game (exactly) and
        hit "Enter!
      </p>

      <p>Try guessing without checking Board Game Geek.</p>
      <p>There will be one new board game to guess every day.</p>
      <h1 class="font-londrina text-xl my-2">Have feedback?</h1>
      <p>
        Contact me over email (hello at letsplayboard.games) or
        <a
          href="https://twitter.com/BoardGamesApp"
          target="_blank"
          class="text-blue-300"
          >Twitter</a
        >.
      </p>
    </modal>
  </div>
</template>

<script>
import { VTooltip } from 'v-tooltip';

import HintTile from './guess_board_game/HintTile.vue';
import Modal from './Modal.vue';

const SCORE_BONUS = {
  designer: 20,
  publisher: 20,
  year: 20,
  artist: 20,
  playerCount: 10,
  duration: 10,
  description: 120,
  mechanisms: 30,
  categories: 30,
  rank: 20,
  firstLetter: 20,
};

const EMOJI_MAP = {
  designer: '🧙',
  publisher: '🎬',
  year: '📅',
  artist: '🧑‍🎨',
  playerCount: '👪',
  duration: '⏰',
  description: '📋',
  mechanisms: '⚙️',
  categories: '🗄️',
  rank: '📈',
  firstLetter: '🔤',
  guess: '❌',
  correct: '✅',
};

export default {
  directives: { tooltip: VTooltip },
  components: { HintTile, Modal },
  props: {
    details: { type: Object, required: true },
  },
  data() {
    return {
      layout: 'homepage.njk',
      current: '',
      guesses: [],
      revealed: {},
      score: 0,
      copied: false,
      solution: [],
    };
  },
  methods: {
    onSubmit() {
      if (!this.current) return;
      if (this.guesses.length < 3) {
        this.addGuess(this.current.trim());
        this.current = '';
      } else {
        console.log(this.guesses);
      }
    },
    reveal(hint) {
      const existing = JSON.parse(localStorage.getItem('guessing')) || {};
      if (existing.revealed[hint] !== undefined) return;
      this.$set(this.revealed, hint, true);
      existing.revealed = this.revealed;
      this.solution.push({ hint });
      existing.solution = this.solution;
      localStorage.setItem('guessing', JSON.stringify(existing));
    },
    addGuess(guess) {
      this.guesses.push(guess);
      if (this.isCorrect(guess)) {
        this.score = 100;
      }
      const existing = JSON.parse(localStorage.getItem('guessing')) || {};
      existing.guesses = this.guesses;
      existing.game = this.game;
      this.solution.push({ guess });
      existing.solution = this.solution;
      localStorage.setItem('guessing', JSON.stringify(existing));
      try {
        if (this.guesses.length === 3 || this.isCorrect(guess)) {
          plausible('Game', {
            props: {
              won: this.isCorrect(guess),
              score: this.finalScore,
              guesses: this.guesses.length,
            },
          });
        }
      } catch (error) {
        console.error(error);
      }
    },
    isCorrect(guess) {
      const primary =
        guess &&
        guess.replace(/[^0-9a-z]/gi, '').toLowerCase() ===
          this.game.replace(/[^0-9a-z]/gi, '').toLowerCase();
      if (primary) return true;

      return (
        guess &&
        this.details.alternative &&
        this.details.alternative.length &&
        this.alternatives.indexOf(guess.toLowerCase()) > -1
      );
    },
    isRevealed(hint) {
      return this.revealed[hint];
    },
    getShareBrag() {
      let attempt = 1;
      let share = `Guess the Board Game\n${this.details.day || ''}\nScore: ${
        this.finalScore
      }`;
      if (!this.solution) return share;
      share = `${share}\n#${attempt} `;
      for (let step of this.solution) {
        if (step.hint) {
          const emoji = EMOJI_MAP[step.hint];
          share = `${share}${emoji}`;
        } else if (step.guess) {
          if (this.isCorrect(step.guess)) {
            const emoji = EMOJI_MAP['correct'];
            share = `${share} → ${emoji}`;
          } else {
            const emoji = EMOJI_MAP['guess'];
            attempt += 1;
            share = `${share} → ${emoji}\n\n#${attempt} `;
          }
        }
      }
      return share;
    },
    shareScore() {
      navigator.clipboard.writeText(this.getShareBrag());
      this.copied = true;
      try {
        plausible('Shared');
      } catch (error) {
        console.error(error);
      }
    },
  },
  mounted() {
    const existing = JSON.parse(localStorage.getItem('guessing'));
    if (!existing) {
      this.$refs.modal.show();
    }
    if (existing && existing.game === this.game) {
      this.guesses = existing.guesses || [];
      this.revealed = existing.revealed || {};
      this.score = existing.score || 0;
      this.solution = existing.solution || [];
    } else {
      localStorage.setItem(
        'guessing',
        JSON.stringify({
          game: this.game,
          guesses: [],
          revealed: {},
          score: 0,
          solution: [],
        })
      );
    }
  },
  computed: {
    game() {
      return this.details.game;
    },
    alternatives() {
      return this.details.alternative.map((g) => g.toLowerCase());
    },
    canGuess() {
      return this.guesses.length < 3 && !this.won;
    },
    won() {
      return (
        this.guesses.length < 4 &&
        this.guesses.filter((g) => this.isCorrect(g)).length > 0
      );
    },
    guessPlaceholder() {
      if (this.won) {
        return 'You won!';
      } else if (this.canGuess) {
        return 'Enter your guess';
      } else {
        return 'No guesses left';
      }
    },
    fixedGuesses() {
      const max = this.guesses.slice(0, 3);
      if (max.length < 3) {
        return max.concat(Array(3 - max.length));
      } else {
        return max;
      }
    },
    durationFixed() {
      const { duration } = this.details;
      if (duration.endsWith('min')) {
        return duration;
      } else {
        return `${duration} min`;
      }
    },
    rankings() {
      const rankings = [];
      for (const rankKey in this.details.rank) {
        rankings.push(`${rankKey}: ${this.details.rank[rankKey]}`);
      }
      return rankings.join(', ');
    },
    scoreBonus() {
      return SCORE_BONUS;
    },
    finalScore() {
      const base = this.won ? 100 : 0;
      if (!base) return base;
      let score = base;
      for (const key in this.scoreBonus) {
        if (!this.revealed[key]) {
          score = score + this.scoreBonus[key];
        }
      }
      if (this.guesses.length === 1) score = score * 2.5;
      if (this.guesses.length === 2) score = score * 2;
      return score;
    },
  },
};
</script>

<style lang="postcss">
@keyframes tada {
  from {
    transform: scale3d(1, 1, 1);
  }

  10%,
  20% {
    transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);
  }

  30%,
  50%,
  70%,
  90% {
    transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);
  }

  40%,
  60%,
  80% {
    transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);
  }

  to {
    transform: scale3d(1, 1, 1);
  }
}

.tada {
  animation-name: tada;
}

.tada-animation {
  animation: tada 1s;
}
.padding-notch {
  padding-left: env(safe-area-inset-left);
}

.tooltip {
  display: block !important;
  z-index: 10000;
  max-height: 70%;
  overflow-y: auto;

  .tooltip-inner {
    background: black;
    color: white;
    border-radius: 16px;
    padding: 5px 10px 4px;
  }

  .tooltip-arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 5px;
    border-color: black;
    z-index: 1;
  }

  &[x-placement^='top'] {
    margin-bottom: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 0 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      bottom: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='bottom'] {
    margin-top: 5px;

    .tooltip-arrow {
      border-width: 0 5px 5px 5px;
      border-left-color: transparent !important;
      border-right-color: transparent !important;
      border-top-color: transparent !important;
      top: -5px;
      left: calc(50% - 5px);
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  &[x-placement^='right'] {
    margin-left: 5px;

    .tooltip-arrow {
      border-width: 5px 5px 5px 0;
      border-left-color: transparent !important;
      border-top-color: transparent !important;
      border-bottom-color: transparent !important;
      left: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &[x-placement^='left'] {
    margin-right: 5px;

    .tooltip-arrow {
      border-width: 5px 0 5px 5px;
      border-top-color: transparent !important;
      border-right-color: transparent !important;
      border-bottom-color: transparent !important;
      right: -5px;
      top: calc(50% - 5px);
      margin-left: 0;
      margin-right: 0;
    }
  }

  &.popover {
    $color: #f9f9f9;

    .popover-inner {
      background: $color;
      color: black;
      padding: 24px;
      border-radius: 5px;
      box-shadow: 0 5px 30px rgba(black, 0.1);
    }

    .popover-arrow {
      border-color: $color;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }
}
</style>
